import React from "react";
import {
  useGetHealthTargetsQuery,
  useGetInvestmentGoalsQuery,
  useGetSavingsGoalsQuery,
} from "../../../services";
import { localToday } from "../../../utils";
import { GOALS_SETTINGS_PATH } from "../../Settings/constants";
import { HEALTH_TARGET_FIELDS } from "../../Settings/GoalsSection/healthTargetsConfig";
import { ManualInvestmentsHint } from "./ManualInvestmentsHint";
import { PlanNoTargetsBanner } from "./PlanNoTargetsBanner";
import { PlanGoalsSection } from "./PlanGoalsSection";
import { PlanMetricItem } from "./PlanMetricItem";
import { PlanSkeletonGrid } from "./PlanSkeletonGrid";
import {
  justEndedGoalsOf,
  planGoalsOf,
  pastGoalCountOf,
} from "./planGoalLists";
import {
  burnRateMetric,
  debtToAssetMetric,
  runwayMetric,
  savingsRateMetric,
  targetTextOf,
} from "./planMetrics";
import { YourPlanSectionProps } from "./types";
import {
  PlanGrid,
  PlanHeader,
  PlanHeaderLink,
  PlanHeaderTitle,
  PlanSection,
} from "../styles";

export const YourPlanSection: React.FC<YourPlanSectionProps> = React.memo(
  ({
    income,
    expenses,
    yearTotals,
    investments,
    accounts,
    savings,
    liabilities,
    numberOfMonths,
    valuation,
    hasManualInvestmentAccounts,
  }) => {
    const { data: targets, isLoading: targetsLoading } =
      useGetHealthTargetsQuery();
    const { data: savingsGoals, isError: savingsGoalsError } =
      useGetSavingsGoalsQuery();
    const { data: investmentGoals, isError: investmentGoalsError } =
      useGetInvestmentGoalsQuery();

    const hasActivity = [
      income,
      expenses,
      investments,
      accounts,
      savings,
      liabilities,
    ].some((value) => value !== 0 && value !== null);
    const today = localToday();
    const isTargetUnavailable = !targetsLoading && targets === undefined;

    const savingsRateTarget = targets?.savingsRateMinPct ?? null;
    const runwayTarget = targets?.runwayMinMonths ?? null;
    const debtToAssetTarget = targets?.debtToAssetMax ?? null;
    const burnRateTarget = targets?.burnRateMaxPct ?? null;
    const hasNoTargets =
      targets !== undefined &&
      HEALTH_TARGET_FIELDS.every(({ name }) => targets[name] === null);

    const savingsRate = savingsRateMetric({
      year: yearTotals,
      today,
      target: savingsRateTarget,
      hasActivity,
    });
    const runway = runwayMetric({
      investments,
      allTimeExpenses: expenses,
      numberOfMonths,
      valuation,
      target: runwayTarget,
      hasActivity,
    });
    const debtToAsset = debtToAssetMetric({
      liabilities,
      accounts,
      investments,
      savings,
      valuation,
      target: debtToAssetTarget,
      hasActivity,
    });
    const burnRate = burnRateMetric({
      year: yearTotals,
      today,
      target: burnRateTarget,
      hasActivity,
    });

    const incomeHint = hasManualInvestmentAccounts ? (
      <ManualInvestmentsHint />
    ) : null;
    const goalLists = {
      savingsGoals: savingsGoals ?? [],
      investmentGoals: investmentGoals ?? [],
      today,
    };
    const goals = planGoalsOf(goalLists);
    const justEndedGoals = justEndedGoalsOf(goalLists);
    const pastGoalCount = pastGoalCountOf(goalLists);
    const goalsLoading =
      (savingsGoals === undefined && !savingsGoalsError) ||
      (investmentGoals === undefined && !investmentGoalsError);
    const goalsError = savingsGoalsError || investmentGoalsError;
    const hasNoGoals =
      !goalsLoading &&
      !goalsError &&
      goals.length === 0 &&
      justEndedGoals.length === 0;

    const planGrid = targetsLoading ? (
      <PlanSkeletonGrid />
    ) : (
      <PlanGrid>
        <PlanMetricItem
          title="Savings rate"
          metric={savingsRate}
          targetMetric="savingsRate"
          targetText={targetTextOf("savingsRate", savingsRateTarget)}
          isTargetUnavailable={isTargetUnavailable}
          hint={incomeHint}
        />
        <PlanMetricItem
          title="Investment runway"
          metric={runway}
          targetMetric="runway"
          targetText={targetTextOf("runway", runwayTarget)}
          isTargetUnavailable={isTargetUnavailable}
        />
        <PlanMetricItem
          title="Debt-to-asset"
          metric={debtToAsset}
          targetMetric="debtToAsset"
          targetText={targetTextOf("debtToAsset", debtToAssetTarget)}
          isTargetUnavailable={isTargetUnavailable}
        />
        <PlanMetricItem
          title="Spending / income"
          metric={burnRate}
          targetMetric="burnRate"
          targetText={targetTextOf("burnRate", burnRateTarget)}
          isTargetUnavailable={isTargetUnavailable}
          hint={incomeHint}
        />
      </PlanGrid>
    );

    return (
      <PlanSection aria-labelledby="your-plan-title">
        <PlanHeader>
          <PlanHeaderTitle id="your-plan-title">Your plan</PlanHeaderTitle>
          <PlanHeaderLink href={GOALS_SETTINGS_PATH}>Manage</PlanHeaderLink>
        </PlanHeader>

        {hasNoTargets && <PlanNoTargetsBanner />}

        {planGrid}

        <PlanGoalsSection
          rows={goals}
          justEndedGoals={justEndedGoals}
          isLoading={goalsLoading}
          isError={goalsError}
          isEmpty={hasNoGoals}
          pastGoalCount={pastGoalCount}
        />
      </PlanSection>
    );
  },
);

YourPlanSection.displayName = "YourPlanSection";
